import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {getMetricPrecision} from '../formatters';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface WindChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
}

export const windChart = (options: WindChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
  } = options;

  const series: SeriesConfig[] = [
    {
      id: 'wind-speed',
      type: 'line',
      dataKey: 'FF',
      name: 'Wind',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
    {
      id: 'wind-gust',
      type: 'area',
      dataKey: 'FXY',
      name: 'Gust',
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    },
  ];

  if (comparisonData) {
    series.push({
      id: 'wind-comparison',
      type: 'line',
      dataKey: 'FF_COMP',
      name: comparisonLabel ?? 'Comparison',
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  // Custom tooltip formatter that includes wind direction
  const windPrecision = getMetricPrecision('FF', units);
  const windUnitLabel = Units.getUnitLabel(units, 'FF');
  const formatDirection = (dataPoint?: Record<string, unknown>, dirKey = 'DD') => {
    const dir = dataPoint?.[dirKey];
    if (dir == null || typeof dir !== 'number') return '';
    const label = Units.getPolarAngleFromDegrees(dir);
    return label ? ` (${label})` : '';
  };
  const customFormatters: Record<string, (value: number, dataPoint?: Record<string, unknown>) => string> = {
    'wind-speed': (value: number, dataPoint?: Record<string, unknown>) => {
      return `${value.toFixed(windPrecision)} ${windUnitLabel}${formatDirection(dataPoint, 'DD')}`;
    },
    'wind-gust': (value: number, dataPoint?: Record<string, unknown>) => {
      return `${value.toFixed(windPrecision)} ${windUnitLabel}${formatDirection(dataPoint, 'DXY')}`;
    },
  };

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [
      createYAxisConfig({
        id: 'wind',
        metric: 'FF',
        units,
        theme,
        domain: [0, 'auto'],
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'FF',
      units,
      theme,
      dateTimeConfig,
      customFormatters,
    }),
    legend: createLegendConfig(series.length > 2, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

/**
 * Wind speed chart for monthly/annual analysis reports.
 * Shows wind speed and gust as separate colored lines.
 */
export interface WindSpeedReportChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  height?: number;
}

export const windSpeedReportChart = (
  options: WindSpeedReportChartOptions,
): ChartConfig => {
  const {data, theme, timezone, units, height = 250} = options;

  return {
    height,
    data,
    series: [
      {
        id: 'wind-speed',
        type: 'line',
        dataKey: 'FF',
        name: 'Wind',
        color: theme.colors.primary,
        yAxisIndex: 0,
        interpolation: 'monotone',
        lineWidth: 2,
        showMarkers: false,
        zIndex: 2,
      },
      {
        id: 'wind-gust',
        type: 'line',
        dataKey: 'FXY',
        name: 'Gust',
        color: theme.colors.error,
        yAxisIndex: 0,
        interpolation: 'monotone',
        lineWidth: 2,
        dashArray: '5 3',
        showMarkers: false,
        zIndex: 1,
      },
    ],
    xAxis: createXAxisConfig({
      type: 'datetime',
      timezone,
      granularity: 'D',
      theme,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'wind',
        metric: 'FF',
        units,
        theme,
        domain: [0, 'auto'],
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity: 'D',
      metric: 'FF',
      units,
      theme,
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
